import * as THREE from '@phdsoft/three';

export interface ElementMapEntry {
  elementId: string;
  regionId?: string;
  sectionId?: string;
  typeId?: string;
}

/**
 * Per-mesh mask: hides fragments whose `_EL_IDX` value is marked masked.
 * The lightsection meshes produced by the conversor carry an `_EL_IDX` Uint16
 * vertex attribute plus `userData.elementMap` indexed by that value. We render
 * the same merged mesh but discard fragments belonging to elements that have
 * been cut/replaced by a repair.
 *
 * `_EL_IDX` is per-mesh local, so each merged mesh needs its own MaskHandle.
 */
export interface MaskHandle {
  readonly mesh: THREE.Mesh;
  readonly elementMap: ElementMapEntry[];
  /** The un-patched material the mesh would use without the mask (a shared static or vcMaterial). */
  readonly baseMaterial: THREE.Material;
  /** Swap the base material (e.g. to the shared vertex-colour material) while keeping the mask. */
  setBaseMaterial(material: THREE.Material): void;
  /** Replace the masked set. Index values outside [0, elementMap.length) are ignored. */
  setMasked(elIdxValues: Iterable<number>): void;
  /** Mask a single element by its elementId. Returns true if the element belongs to this mesh. */
  maskElement(elementId: string): boolean;
  /** Look up the local _EL_IDX for an elementId, or -1 if not in this mesh. */
  indexOfElement(elementId: string): number;
  clear(): void;
  /** Restores the current base material and releases GPU resources. */
  dispose(): void;
}

// GLSL reserves identifiers containing "__" so we cannot bind directly to the
// "__el_idx" attribute name that Three.js' GLTFLoader gives us. Alias it under
// a safe name on the geometry — both names will reference the same buffer.
const EL_IDX_SOURCE_NAMES = ['__el_idx', '_EL_IDX', '_el_idx', '__EL_IDX'];
const EL_IDX_SHADER_NAME = 'aElIdx';

const VERTEX_DECLS = `attribute float ${EL_IDX_SHADER_NAME};\nvarying float vElIdx;\n`;
const VERTEX_ASSIGN = `vElIdx = ${EL_IDX_SHADER_NAME};`;
const FRAGMENT_DECLS = 'uniform sampler2D uMaskTex;\nuniform float uMaskSize;\nvarying float vElIdx;\n';
const FRAGMENT_DISCARD = 'if (texture2D(uMaskTex, vec2((vElIdx + 0.5) / uMaskSize, 0.5)).r > 0.5) discard;';

const MASK_HANDLE_KEY = '__lsMaskHandle';

/**
 * userData counter bumped whenever a mesh's mask data (__lsMaskData) changes or is
 * removed. The occlusion culler puts it in its idle safety-frame signature: mask
 * changes (repair mods punching holes) never call requestRender, and the sections
 * behind a new hole must be re-culled with the camera unchanged.
 */
const MASK_VERSION_KEY = '__lsMaskVersion';

function bumpMaskVersion(mesh: THREE.Mesh): void {
  mesh.userData[MASK_VERSION_KEY] = ((mesh.userData[MASK_VERSION_KEY] as number) ?? 0) + 1;
}

/** Every live MaskHandle. Lets the shared-material toggles reach the per-mesh patched clones. */
const activeHandles = new Set<MaskHandle>();

/**
 * Copy the render-state flags the X-ray / measurement / Transparence toggles mutate
 * on the shared static materials (visible, opacity, transparent, depthWrite) onto
 * every patched clone. Call after mutating GraphicsMaterials statics.
 */
export function syncMaskedMaterialsFromBase(): void {
  for (const handle of activeHandles) {
    const patched = handle.mesh.material;
    if (Array.isArray(patched)) continue;
    const base = handle.baseMaterial;
    patched.visible = base.visible;
    patched.opacity = base.opacity;
    patched.transparent = base.transparent;
    patched.depthWrite = base.depthWrite;
  }
}

/** The handle owning `mesh`'s material, if the mesh is a masked lightsection mesh. */
export function getLightSectionMaskHandle(mesh: THREE.Mesh): MaskHandle | undefined {
  return mesh.userData?.[MASK_HANDLE_KEY] as MaskHandle | undefined;
}

/**
 * Assign a material to a lightsection mesh. Masked meshes keep their mask: the
 * material becomes the new base and is re-patched. Other meshes get it directly.
 */
export function setLightSectionMeshMaterial(mesh: THREE.Mesh, material: THREE.Material): void {
  const handle = getLightSectionMaskHandle(mesh);
  if (handle) {
    handle.setBaseMaterial(material);
  } else {
    mesh.material = material;
  }
}

/** The material a swap should later restore: the base for masked meshes, mesh.material otherwise. */
export function getLightSectionMeshBaseMaterial(mesh: THREE.Mesh): THREE.Material {
  const handle = getLightSectionMaskHandle(mesh);
  if (handle) return handle.baseMaterial;
  return Array.isArray(mesh.material) ? mesh.material[0] : mesh.material;
}

function findElementIndexAttribute(geom: THREE.BufferGeometry): THREE.BufferAttribute | THREE.InterleavedBufferAttribute | null {
  for (const name of EL_IDX_SOURCE_NAMES) {
    const attr = geom.getAttribute(name);
    if (attr) return attr as THREE.BufferAttribute | THREE.InterleavedBufferAttribute;
  }
  return null;
}

/**
 * Wraps a merged lightsection mesh with a shader-level mask. Returns null if the
 * mesh has no element-index attribute (e.g. legacy GLBs) or no elementMap.
 */
export function createMaskHandle(mesh: THREE.Mesh): MaskHandle | null {
  const geom = mesh.geometry as THREE.BufferGeometry;
  const idxAttr = findElementIndexAttribute(geom);
  if (!idxAttr) return null;

  // Alias the attribute under a GLSL-safe name so the shader can bind it.
  // Three.js attribute lookup is by exact name; aliasing is just adding a second
  // entry that references the same underlying buffer — no memory duplication.
  if (!geom.getAttribute(EL_IDX_SHADER_NAME)) {
    geom.setAttribute(EL_IDX_SHADER_NAME, idxAttr);
  }

  const elementMap = (mesh.userData?.elementMap as ElementMapEntry[]) ?? null;
  if (!elementMap || elementMap.length === 0) return null;

  const size = elementMap.length;
  const data = new Uint8Array(size);
  const maskTex = new THREE.DataTexture(data, size, 1, THREE.RedFormat, THREE.UnsignedByteType);
  maskTex.minFilter = THREE.NearestFilter;
  maskTex.magFilter = THREE.NearestFilter;
  maskTex.wrapS = THREE.ClampToEdgeWrapping;
  maskTex.wrapT = THREE.ClampToEdgeWrapping;
  maskTex.needsUpdate = true;

  const originalMaterial = mesh.material;
  let baseMaterial = (Array.isArray(originalMaterial) ? originalMaterial[0] : originalMaterial) as THREE.Material;

  const patchMaterial = (base: THREE.Material): THREE.Material => {
    const patched = base.clone() as THREE.Material & { onBeforeCompile: (shader: any) => void };
    patched.onBeforeCompile = (shader: any) => {
      if (shader.vertexShader.includes('#include <common>')) {
        shader.vertexShader = shader.vertexShader.replace(
          '#include <common>',
          '#include <common>\n' + VERTEX_DECLS,
        );
      } else {
        shader.vertexShader = VERTEX_DECLS + shader.vertexShader;
      }
      if (shader.vertexShader.includes('#include <begin_vertex>')) {
        shader.vertexShader = shader.vertexShader.replace(
          '#include <begin_vertex>',
          '#include <begin_vertex>\n' + VERTEX_ASSIGN,
        );
      } else {
        shader.vertexShader = shader.vertexShader.replace(
          'void main() {',
          'void main() {\n  ' + VERTEX_ASSIGN,
        );
      }

      if (shader.fragmentShader.includes('#include <common>')) {
        shader.fragmentShader = shader.fragmentShader.replace(
          '#include <common>',
          '#include <common>\n' + FRAGMENT_DECLS,
        );
      } else {
        shader.fragmentShader = FRAGMENT_DECLS + shader.fragmentShader;
      }
      shader.fragmentShader = shader.fragmentShader.replace(
        'void main() {',
        'void main() {\n  ' + FRAGMENT_DISCARD,
      );

      shader.uniforms.uMaskTex = { value: maskTex };
      shader.uniforms.uMaskSize = { value: size };
    };
    return patched;
  };

  let patched = patchMaterial(baseMaterial);
  mesh.material = patched;

  // Expose mask state on userData so the CPU-side occlusion rasterizer can
  // skip masked triangles. Two coupled fields:
  //   __lsMaskData: Uint8Array indexed by _EL_IDX (0 = visible, 255 = masked)
  //   __lsElIdxAttr: the buffer attribute carrying _EL_IDX per vertex
  // The rasterizer reads vertex 0's _EL_IDX for each triangle (all 3 verts of a
  // triangle share the same value because stampElementIndex fills per-element).
  mesh.userData.__lsMaskData = data;
  mesh.userData.__lsElIdxAttr = idxAttr;
  bumpMaskVersion(mesh);

  const elementIdToIdx = new Map<string, number>();
  for (let i = 0; i < elementMap.length; i++) {
    if (elementMap[i]?.elementId) elementIdToIdx.set(elementMap[i].elementId, i);
  }

  const handle: MaskHandle = {
    mesh,
    elementMap,
    get baseMaterial() {
      return baseMaterial;
    },
    setBaseMaterial(material) {
      if (material === baseMaterial) return;
      baseMaterial = material;
      const previous = patched;
      patched = patchMaterial(material);
      mesh.material = patched;
      previous.dispose();
    },
    setMasked(elIdxValues) {
      data.fill(0);
      for (const idx of elIdxValues) {
        if (idx >= 0 && idx < size) data[idx] = 255;
      }
      maskTex.needsUpdate = true;
      bumpMaskVersion(mesh);
    },
    maskElement(elementId) {
      const idx = elementIdToIdx.get(elementId);
      if (idx === undefined) return false;
      data[idx] = 255;
      maskTex.needsUpdate = true;
      bumpMaskVersion(mesh);
      return true;
    },
    indexOfElement(elementId) {
      return elementIdToIdx.get(elementId) ?? -1;
    },
    clear() {
      data.fill(0);
      maskTex.needsUpdate = true;
      bumpMaskVersion(mesh);
    },
    dispose() {
      activeHandles.delete(handle);
      delete mesh.userData[MASK_HANDLE_KEY];
      mesh.material = baseMaterial;
      delete mesh.userData.__lsMaskData;
      delete mesh.userData.__lsElIdxAttr;
      bumpMaskVersion(mesh); // kept (not deleted) so the counter only grows
      patched.dispose();
      maskTex.dispose();
    },
  };

  mesh.userData[MASK_HANDLE_KEY] = handle;
  activeHandles.add(handle);
  return handle;
}

/**
 * Walks a lightSection group (LightSection > SurfaceTypes > [SurfaceTypesMesh + typeId-meshes])
 * and creates a MaskHandle for each merged mesh that carries `_EL_IDX`.
 */
export function createMaskHandlesForLightSection(lightSectionGroup: THREE.Object3D): MaskHandle[] {
  const handles: MaskHandle[] = [];
  lightSectionGroup.traverse(obj => {
    if ((obj as THREE.Mesh).isMesh) {
      const handle = createMaskHandle(obj as THREE.Mesh);
      if (handle) handles.push(handle);
    }
  });
  return handles;
}
